/**
 * About Page - About Bitcoin Loans and Mortgages
 */

import { Link } from 'react-router-dom'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { useAccount } from 'wagmi'
import { SEO, generateBreadcrumbSchema, organizationSchema } from '../components/common/SEO'
import { Button } from '../components/common/Button'
import { Card, CardContent } from '../components/common/Card'
import { 
  BlockSecureIllustration,
  CoinsLockIllustration,
  BitcoinWalletNoteIllustration
} from '@bitcoin-com/react-components/src/components/mini-illustrations'

export function AboutPage() {
  const { openConnectModal } = useConnectModal()
  const { isConnected } = useAccount()

  const aboutBreadcrumb = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'About', url: '/about' },
  ])

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      organizationSchema,
      aboutBreadcrumb,
    ],
  }

  return (
    <>
      <SEO
        title="About Bitcoin.com Loans & Mortgages"
        description="Self-custodial, crypto-backed financing powered by Bitcoin.com and Anvil Protocol. Learn how we enable you to unlock the value of your Bitcoin without giving up custody."
        canonical="/about"
        jsonLd={aboutJsonLd}
      />
      
      {/* Hero Section */}
      <section className="px-m md:px-xl py-[60px] md:py-[80px] bg-shades-white">
        <div className="max-w-[900px] mx-auto text-center">
          <h1 className="font-display font-black text-[44px] md:text-[56px] lg:text-[64px] leading-snug uppercase text-text-primary mb-m">
            About Bitcoin Loans & Mortgages
          </h1>
          <p className="text-body-lg md:text-heading-sm text-text-secondary leading-relaxed max-w-[700px] mx-auto">
            Self-custodial, crypto-backed financing powered by Bitcoin.com and Anvil Protocol
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="px-m md:px-xl py-[60px] md:py-[80px] bg-shades-extra-light">
        <div className="max-w-[900px] mx-auto">
          <h2 className="font-display font-black text-heading-xl uppercase text-text-primary mb-l text-center">
            Introduction
          </h2>
          
          <div className="space-y-l text-body-lg text-text-secondary leading-relaxed">
            <p>
              Bitcoin.com Loans & Mortgages is a revolutionary financial platform that enables you to unlock the value of your bitcoin and cryptocurrency holdings <strong>without giving up custody of your assets</strong>. In partnership with <a href="https://anvilapplication.com" target="_blank" rel="noopener noreferrer" className="text-primary-100 hover:underline">Anvil Protocol</a>, we've created a system of Ethereum-based smart contracts that manages collateral and issues fully secured credit through Letters of Credit (LOCs).
            </p>
            
            <p>
              Unlike traditional custodial bitcoin loans where you must transfer your assets to a third party—exposing you to counterparty risk, exchange hacks, and potential loss of funds—our platform lets you maintain complete control of your bitcoin while still accessing the liquidity you need for real-world expenses like mortgages, business loans, and major purchases.
            </p>

            <p>
              Through our partnership with traditional banks, Bitcoin.com and Anvil have created a bridge between decentralized finance (DeFi) and traditional finance (TradFi), allowing banks to accept your blockchain-secured LOCs as collateral for fiat loans and mortgages.
            </p>
          </div>
        </div>
      </section>

      {/* Why Self-Custody Matters */}
      <section className="px-m md:px-xl py-[60px] md:py-[80px] bg-shades-white">
        <div className="max-w-[900px] mx-auto">
          <h2 className="font-display font-black text-heading-xl uppercase text-text-primary mb-l text-center">
            Why Self-Custody Matters
          </h2>

          <div className="space-y-l text-body-lg text-text-secondary leading-relaxed mb-xl">
            <p>
              The history of centralized crypto lending platforms is littered with catastrophic failures. From Mt. Gox to Celsius Network, BlockFi to FTX, users who trusted their bitcoin and crypto to custodial lending platforms have lost billions of dollars due to:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-m mb-xl">
            <Card padding="lg">
              <CardContent className="text-center">
                <div className="flex justify-center mb-m">
                  <div className="w-[64px] h-[64px]">
                    <CoinsLockIllustration className="w-full h-full" />
                  </div>
                </div>
                <h3 className="font-sans font-semiBold text-body-lg mb-s text-text-primary">
                  Exchange Hacks
                </h3>
                <p className="text-body-sm text-text-secondary leading-relaxed">
                  Centralized platforms are honeypots for hackers, with billions lost to security breaches
                </p>
              </CardContent>
            </Card>

            <Card padding="lg">
              <CardContent className="text-center">
                <div className="flex justify-center mb-m">
                  <div className="w-[64px] h-[64px]">
                    <BitcoinWalletNoteIllustration className="w-full h-full" />
                  </div>
                </div>
                <h3 className="font-sans font-semiBold text-body-lg mb-s text-text-primary">
                  Rehypothecation
                </h3>
                <p className="text-body-sm text-text-secondary leading-relaxed">
                  Platforms lend out your collateral multiple times, creating systemic risk and insolvency
                </p>
              </CardContent>
            </Card>

            <Card padding="lg">
              <CardContent className="text-center">
                <div className="flex justify-center mb-m">
                  <div className="w-[64px] h-[64px]">
                    <BlockSecureIllustration className="w-full h-full" />
                  </div>
                </div>
                <h3 className="font-sans font-semiBold text-body-lg mb-s text-text-primary">
                  Mismanagement
                </h3>
                <p className="text-body-sm text-text-secondary leading-relaxed">
                  Poor risk management and fraud have caused multiple high-profile bankruptcies
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-l text-body-lg text-text-secondary leading-relaxed">
            <p>
              <strong>With Bitcoin.com Loans & Mortgages, your bitcoin stays in your wallet.</strong> Your collateral is locked in audited, non-custodial smart contracts on Ethereum, visible on the blockchain at all times. No one can rehypothecate your assets, gamble with your funds, or prevent you from accessing your collateral once your loan is repaid.
            </p>

            <p className="text-heading-sm font-display font-black text-text-primary">
              "Not your keys, not your coins" isn't just a saying—it's the difference between financial sovereignty and catastrophic loss.
            </p>
          </div>
        </div>
      </section>

      {/* Tax Benefits */}
      <section className="px-m md:px-xl py-[60px] md:py-[80px] bg-shades-extra-light">
        <div className="max-w-[900px] mx-auto">
          <h2 className="font-display font-black text-heading-xl uppercase text-text-primary mb-l text-center">
            Tax-Efficient Liquidity
          </h2>

          <div className="space-y-l text-body-lg text-text-secondary leading-relaxed">
            <p>
              In many jurisdictions, <strong>selling bitcoin is a taxable event</strong>, potentially triggering capital gains tax on your profits. However, <strong>borrowing against your bitcoin is typically not considered a taxable event</strong>, allowing you to access liquidity without incurring immediate tax liability.
            </p>

            <div className="bg-secondary-10 border-l-4 border-secondary-100 p-l rounded-sm my-l">
              <h3 className="font-sans font-bold text-body-lg text-text-primary mb-s">
                Example: $100,000 Liquidity Need
              </h3>
              <div className="space-y-s text-body">
                <p>
                  <strong>Option 1 - Sell Bitcoin:</strong>
                  <br />
                  Sell $100,000 of BTC → Pay 20-37% capital gains tax → Permanently lose BTC holdings → Miss future appreciation
                </p>
                <p>
                  <strong>Option 2 - Bitcoin.com Loan:</strong>
                  <br />
                  Borrow $100,000 against BTC collateral → Pay interest only → Keep your BTC → Maintain upside exposure → No immediate tax liability*
                </p>
              </div>
            </div>

            <p>
              This tax efficiency is particularly valuable for long-term bitcoin holders who want to access their wealth for major purchases—like buying a home, starting a business, or funding education—without triggering a taxable sale.
            </p>

            <p className="text-body-sm italic bg-shades-white p-m rounded-sm border border-border">
              <strong>Important Disclaimer:</strong> Tax laws vary by jurisdiction and individual circumstances. This information is for educational purposes only and should not be considered tax advice. Always consult with a qualified tax professional or accountant in your jurisdiction before making financial decisions. Local rules and regulations apply.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-m md:px-xl py-[60px] md:py-[80px] bg-shades-white">
        <div className="max-w-[900px] mx-auto">
          <h2 className="font-display font-black text-heading-xl uppercase text-text-primary mb-l text-center">
            How Bitcoin Loans Work
          </h2>

          <div className="space-y-xl">
            {/* Step 1 */}
            <div className="flex gap-l items-start">
              <div className="flex-shrink-0 w-[48px] h-[48px] rounded-pill bg-secondary-100 flex items-center justify-center">
                <span className="text-shades-white font-bold font-mono text-body-lg">1</span>
              </div>
              <div>
                <h3 className="font-sans font-bold text-heading-sm text-text-primary mb-s">
                  Create a Non-Custodial Vault
                </h3>
                <p className="text-body text-text-secondary leading-relaxed">
                  Connect your wallet and create a secure vault using Anvil Protocol's audited smart contracts. Your vault is a blockchain address you control—your bitcoin never leaves your custody.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-l items-start">
              <div className="flex-shrink-0 w-[48px] h-[48px] rounded-pill bg-secondary-100 flex items-center justify-center">
                <span className="text-shades-white font-bold font-mono text-body-lg">2</span>
              </div>
              <div>
                <h3 className="font-sans font-bold text-heading-sm text-text-primary mb-s">
                  Deposit Collateral
                </h3>
                <p className="text-body text-text-secondary leading-relaxed">
                  Deposit bitcoin (bridged to cbBTC or WBTC) or other crypto assets as collateral. Our platform uses real-time Chainlink price oracles to calculate your borrowing power based on overcollateralization ratios.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-l items-start">
              <div className="flex-shrink-0 w-[48px] h-[48px] rounded-pill bg-secondary-100 flex items-center justify-center">
                <span className="text-shades-white font-bold font-mono text-body-lg">3</span>
              </div>
              <div>
                <h3 className="font-sans font-bold text-heading-sm text-text-primary mb-s">
                  Issue a Letter of Credit
                </h3>
                <p className="text-body text-text-secondary leading-relaxed">
                  Generate a blockchain-secured Letter of Credit (LOC) backed by your collateral. This LOC functions like a guaranteed bank cheque, providing cryptographic proof of your ability to pay.
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-l items-start">
              <div className="flex-shrink-0 w-[48px] h-[48px] rounded-pill bg-secondary-100 flex items-center justify-center">
                <span className="text-shades-white font-bold font-mono text-body-lg">4</span>
              </div>
              <div>
                <h3 className="font-sans font-bold text-heading-sm text-text-primary mb-s">
                  Receive Your Loan or Mortgage
                </h3>
                <p className="text-body text-text-secondary leading-relaxed">
                  Partner banks accept your LOC as collateral and disburse funds in fiat currency (USD, EUR, etc.) for your mortgage, business loan, or personal loan. Your bitcoin remains in your vault throughout the entire process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership */}
      <section className="px-m md:px-xl py-[60px] md:py-[80px] bg-shades-extra-light">
        <div className="max-w-[900px] mx-auto">
          <h2 className="font-display font-black text-heading-xl uppercase text-text-primary mb-l text-center">
            Bitcoin.com × Anvil × Traditional Banks
          </h2>

          <div className="space-y-l text-body-lg text-text-secondary leading-relaxed">
            <p>
              This platform represents a groundbreaking partnership between three key players:
            </p>

            <div className="grid md:grid-cols-3 gap-m my-xl">
              <Card padding="lg">
                <CardContent>
                  <h3 className="font-display font-black text-heading-sm uppercase text-text-primary mb-s">
                    Bitcoin.com
                  </h3>
                  <p className="text-body-sm text-text-secondary leading-relaxed">
                    Leading bitcoin platform bringing user experience, brand trust, and crypto expertise
                  </p>
                </CardContent>
              </Card>

              <Card padding="lg">
                <CardContent>
                  <h3 className="font-display font-black text-heading-sm uppercase text-text-primary mb-s">
                    Anvil Protocol
                  </h3>
                  <p className="text-body-sm text-text-secondary leading-relaxed">
                    Ethereum smart contract infrastructure providing secure, audited collateral management
                  </p>
                </CardContent>
              </Card>

              <Card padding="lg">
                <CardContent>
                  <h3 className="font-display font-black text-heading-sm uppercase text-text-primary mb-s">
                    Partner Banks
                  </h3>
                  <p className="text-body-sm text-text-secondary leading-relaxed">
                    Traditional financial institutions providing fiat loans backed by blockchain LOCs
                  </p>
                </CardContent>
              </Card>
            </div>

            <p>
              Anvil Protocol, governed by the ANVL token and built on governance models pioneered by Compound Labs and OpenZeppelin, provides the technical foundation. The protocol enables transparency and trustless verification of assets, reducing counterparty risk while maintaining maximum efficiency and extensibility.
            </p>

            <p>
              Bitcoin.com brings this technology to mainstream users, offering an intuitive interface for creating vaults, managing collateral, and accessing real-world liquidity—all while keeping bitcoin in self-custody.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="px-m md:px-xl py-[60px] md:py-[80px] bg-shades-white">
        <div className="max-w-[900px] mx-auto">
          <h2 className="font-display font-black text-heading-xl uppercase text-text-primary mb-l text-center">
            Real-World Use Cases
          </h2>

          <div className="space-y-l">
            <Card padding="lg">
              <CardContent>
                <h3 className="font-sans font-bold text-heading-sm text-text-primary mb-s">
                  Bitcoin Mortgages
                </h3>
                <p className="text-body text-text-secondary leading-relaxed">
                  Use your bitcoin holdings as collateral to secure a traditional mortgage without selling your BTC. Keep your exposure to bitcoin appreciation while buying your dream home.
                </p>
              </CardContent>
            </Card>

            <Card padding="lg">
              <CardContent>
                <h3 className="font-sans font-bold text-heading-sm text-text-primary mb-s">
                  Business Loans
                </h3>
                <p className="text-body text-text-secondary leading-relaxed">
                  Entrepreneurs and business owners can access working capital by using their bitcoin as collateral, avoiding dilutive equity financing while maintaining crypto holdings.
                </p>
              </CardContent>
            </Card>

            <Card padding="lg">
              <CardContent>
                <h3 className="font-sans font-bold text-heading-sm text-text-primary mb-s">
                  Instant Liquidity
                </h3>
                <p className="text-body text-text-secondary leading-relaxed">
                  Need cash for an emergency, investment opportunity, or major purchase? Generate an LOC in minutes and receive fiat funding without selling your bitcoin or triggering capital gains.
                </p>
              </CardContent>
            </Card>

            <Card padding="lg">
              <CardContent>
                <h3 className="font-sans font-bold text-heading-sm text-text-primary mb-s">
                  Cross-Platform Transactions
                </h3>
                <p className="text-body text-text-secondary leading-relaxed">
                  LOCs can facilitate immediate transactions across centralized exchanges, decentralized platforms, and Layer 2 networks—providing economic guarantees without custody transfer.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-m md:px-xl py-[60px] md:py-[80px] bg-shades-extra-light">
        <div className="max-w-[900px] mx-auto text-center">
          <h2 className="font-display font-black text-heading-xl md:text-[48px] uppercase text-text-primary mb-m">
            Ready to unlock your bitcoin's value?
          </h2>
          <p className="text-body-lg text-text-secondary mb-xl max-w-[600px] mx-auto leading-relaxed">
            Maintain self-custody, avoid taxable sales, and access the liquidity you need.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-s">
            {isConnected ? (
              <Link to="/vaults">
                <Button variant="primary" size="lg">Create Your First Vault</Button>
              </Link>
            ) : (
              <Button variant="primary" size="lg" onClick={openConnectModal}>
                Connect Wallet to Start
              </Button>
            )}
            <Link to="/">
              <Button variant="default" size="lg">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}

